import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Send, Save, Sparkles, Clock, Wand2, Loader2,
  AlertCircle, Languages, CheckCircle2, AtSign,
  ChevronDown, ChevronUp, Globe, Copy, RefreshCw,
  Share2, ArrowRight, Hash, Plus, X,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MediaUpload } from "./MediaUpload";
import { PlatformPublisher, Platform } from "@/services/platformPublisher";
import { ContentGeneratorDialog } from "@/components/AI/ContentGeneratorDialog";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { CanvaDesignButton } from "@/components/Design/CanvaDesignButton";

// ─── Constants ───────────────────────────────────────────────────────────────

const PLATFORM_META: Record<string, {
  icon: React.ElementType; color: string; bg: string; ring: string;
  limit: number; warning: number; label: string; hint: string;
}> = {
  instagram: {
    icon: Instagram, color: "text-pink-600", bg: "bg-pink-50 dark:bg-pink-950/30",
    ring: "ring-pink-400", limit: 2200, warning: 1800, label: "Instagram",
    hint: "Emojis, line breaks, and up to 30 hashtags work well. First 125 chars visible before 'more'.",
  },
  twitter: {
    icon: Twitter, color: "text-sky-500", bg: "bg-sky-50 dark:bg-sky-950/30",
    ring: "ring-sky-400", limit: 280, warning: 240, label: "X (Twitter)",
    hint: "Hard 280-char limit. Be concise, use 1–2 hashtags, add a hook in the first line.",
  },
  linkedin: {
    icon: Linkedin, color: "text-blue-700", bg: "bg-blue-50 dark:bg-blue-950/30",
    ring: "ring-blue-500", limit: 3000, warning: 2500, label: "LinkedIn",
    hint: "Professional tone. First 210 chars visible before 'see more'. Bullet points perform well.",
  },
  facebook: {
    icon: Facebook, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950/30",
    ring: "ring-blue-400", limit: 63206, warning: 60000, label: "Facebook",
    hint: "Conversational tone. Stories, questions, and videos perform best.",
  },
  threads: {
    icon: AtSign, color: "text-gray-800 dark:text-gray-200", bg: "bg-gray-50 dark:bg-gray-900/30",
    ring: "ring-gray-400", limit: 500, warning: 420, label: "Threads",
    hint: "Conversational, short-form. 500-char limit. Like Twitter but more casual.",
  },
};

const LANGUAGES = [
  { code: "en", label: "English" }, { code: "fr", label: "Français" },
  { code: "pt", label: "Português" }, { code: "es", label: "Español" },
  { code: "ar", label: "العربية" }, { code: "sw", label: "Kiswahili" },
  { code: "zh", label: "中文" }, { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" }, { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" }, { code: "hi", label: "हिन्दी" },
  { code: "yo", label: "Yorùbá" }, { code: "ha", label: "Hausa" },
  { code: "ig", label: "Igbo" },
];

// ─── Caption state helpers ────────────────────────────────────────────────────

interface PlatformCaption {
  content: string;
  customised: boolean; // false = mirrors master
}

function charInfo(text: string, platform: string) {
  const meta = PLATFORM_META[platform];
  if (!meta) return { count: text.length, limit: 0, pct: 0, over: false, near: false };
  const count = text.length;
  return {
    count, limit: meta.limit,
    pct: Math.min(100, (count / meta.limit) * 100),
    over: count > meta.limit,
    near: count > meta.warning && count <= meta.limit,
  };
}

// ─── Per-caption AI toolbar ───────────────────────────────────────────────────

interface CaptionToolbarProps {
  accountId: string | "master";
  platform: string;
  content: string;
  coreHashtags: string[];
  onApply: (text: string) => void;
  onAddHashtag: (tag: string) => void;
  compact?: boolean;
}

function CaptionToolbar({ accountId, platform, content, coreHashtags, onApply, onAddHashtag, compact }: CaptionToolbarProps) {
  const { toast } = useToast();
  const [genOpen, setGenOpen] = useState(false);
  const [transOpen, setTransOpen] = useState(false);
  const [transLang, setTransLang] = useState("fr");
  const [transResult, setTransResult] = useState("");
  const [optimising, setOptimising] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [manualTag, setManualTag] = useState("");

  const addManualTag = () => {
    const tag = manualTag.trim().replace(/^#+/, "");
    if (!tag) return;
    onAddHashtag(tag);
    setManualTag("");
  };

  const handleOptimise = async () => {
    if (!content.trim()) { toast({ title: "No content to optimise", variant: "destructive" }); return; }
    setOptimising(true);
    try {
      const { data, error } = await supabase.functions.invoke("optimize-post", {
        body: { content: content.trim(), platform: platform.toLowerCase() },
      });
      if (error) throw error;
      onApply(data.optimizedContent);
      toast({ title: "Optimised", description: `Score ${data.engagementScore} · ${data.sentiment}` });
    } catch (e: any) {
      toast({ title: "Optimise failed", description: e.message, variant: "destructive" });
    } finally { setOptimising(false); }
  };

  const handleTranslate = async () => {
    if (!content.trim()) { toast({ title: "No content to translate", variant: "destructive" }); return; }
    setTranslating(true);
    setTransResult("");
    try {
      const langLabel = LANGUAGES.find(l => l.code === transLang)?.label ?? transLang;
      const { data, error } = await supabase.functions.invoke("generate-claude-content", {
        body: { prompt: `Translate the following social media caption to ${langLabel}. Return ONLY the translated text, no explanations, no labels.\n\n${content}` },
      });
      if (error) throw error;
      setTransResult(data?.content ?? data?.text ?? "");
    } catch (e: any) {
      toast({ title: "Translation failed", description: e.message, variant: "destructive" });
    } finally { setTranslating(false); }
  };

  const handleGenHashtags = async () => {
    if (!content.trim()) { toast({ title: "Add caption first", variant: "destructive" }); return; }
    setTagging(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-hashtags", {
        body: { content: content.trim(), platform: platform.toLowerCase(), coreHashtags },
      });
      if (error) throw error;
      setHashtags(data.hashtags || []);
    } catch (e: any) {
      toast({ title: "Hashtag generation failed", description: e.message, variant: "destructive" });
    } finally { setTagging(false); }
  };

  const btnSize = compact ? "h-6 px-2 text-[11px]" : "h-7 px-2.5 text-xs";

  return (
    <div className="space-y-2">
      {/* Toolbar row */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* AI Generate */}
        <ContentGeneratorDialog
          platform={platform as Platform}
          onContentGenerated={(c, tags) => { onApply(c); if (tags?.length) setHashtags(tags); }}
        />

        {/* Optimise */}
        <Button variant="outline" size="sm" className={btnSize} onClick={handleOptimise} disabled={optimising || !content.trim()}>
          {optimising ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
          Optimise
        </Button>

        {/* Translate */}
        <Dialog open={transOpen} onOpenChange={v => { setTransOpen(v); if (!v) { setTransResult(""); } }}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className={btnSize} disabled={!content.trim()}>
              <Languages className="w-3 h-3 mr-1" /> Translate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Translate Caption — {PLATFORM_META[platform]?.label ?? platform}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/40 text-sm max-h-28 overflow-y-auto leading-relaxed text-muted-foreground">
                {content || <em>No content yet</em>}
              </div>
              <div>
                <Label className="text-xs">Target language</Label>
                <Select value={transLang} onValueChange={setTransLang}>
                  <SelectTrigger className="mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" size="sm" onClick={handleTranslate} disabled={translating}>
                {translating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Languages className="w-3.5 h-3.5 mr-1.5" />}
                {translating ? "Translating…" : "Translate"}
              </Button>
              {transResult && (
                <div className="space-y-2">
                  <Label className="text-xs">Translation (editable)</Label>
                  <Textarea
                    value={transResult}
                    onChange={e => setTransResult(e.target.value)}
                    rows={5}
                    className="resize-none text-sm"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" onClick={() => { onApply(transResult); setTransOpen(false); toast({ title: "Translation applied" }); }}>
                      Apply <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(transResult); toast({ title: "Copied" }); }}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Hashtags */}
        <Button variant="outline" size="sm" className={btnSize} onClick={handleGenHashtags} disabled={tagging || !content.trim()}>
          {tagging ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
          # Tags
        </Button>
      </div>

      {/* AI-generated hashtag chips */}
      {hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {hashtags.map((tag, i) => (
            <Badge
              key={i}
              variant="secondary"
              className="cursor-pointer hover:bg-primary/20 text-xs"
              onClick={() => { onAddHashtag(tag); setHashtags(prev => prev.filter((_, j) => j !== i)); }}
            >
              #{tag} <Plus className="w-2.5 h-2.5 ml-0.5" />
            </Badge>
          ))}
          <button
            className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
            onClick={() => setHashtags([])}
          >
            clear
          </button>
        </div>
      )}

      {/* Manual hashtag input */}
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            value={manualTag}
            onChange={e => setManualTag(e.target.value.replace(/\s/g, ""))}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addManualTag(); } }}
            placeholder="Type hashtag, press Enter"
            className="pl-7 h-7 text-xs"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs shrink-0"
          onClick={addManualTag}
          disabled={!manualTag.trim()}
        >
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface PostManagerProps {
  initialContent?: string;
  onInitialContentConsumed?: () => void;
}

export const PostManager = ({ initialContent, onInitialContentConsumed }: PostManagerProps = {}) => {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();

  const [accounts, setAccounts] = useState<any[]>([]);
  const [workflows, setWorkflows] = useState<any[]>([]);
  useEffect(() => { fetchAccounts(); fetchWorkflows(); }, []);

  useEffect(() => {
    if (initialContent) {
      setMasterContent(initialContent);
      onInitialContentConsumed?.();
    }
  }, [initialContent]);

  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [masterContent, setMasterContent] = useState("");
  const [captions, setCaptions] = useState<Record<string, PlatformCaption>>({});
  const [activeTab, setActiveTab] = useState<string>("master");

  const [scheduledFor, setScheduledFor] = useState("");
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [aiAssisted, setAiAssisted] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [assignNote, setAssignNote] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [bestTimes, setBestTimes] = useState<string[]>([]);
  const [loadingBestTimes, setLoadingBestTimes] = useState(false);
  const [postType, setPostType] = useState<string>("standard");
  const [threadTweets, setThreadTweets] = useState<string[]>(["", ""]);
  const [pollOptions, setPollOptions] = useState<string[]>(["", ""]);
  const [pollDuration, setPollDuration] = useState(1440); // minutes

  const selectedAccounts = accounts.filter(a => selectedAccountIds.includes(a.id));

  // Derive which post types are available for the selected platforms
  const availablePostTypes = (() => {
    const platforms = selectedAccounts.map(a => a.platform);
    const types: { id: string; label: string; icon: string; platforms: string[] }[] = [
      { id: "standard", label: "Post", icon: "📝", platforms: ["instagram", "facebook", "twitter", "linkedin", "threads", "tiktok", "youtube", "pinterest"] },
      { id: "reel", label: "Reel", icon: "🎬", platforms: ["instagram"] },
      { id: "story", label: "Story", icon: "⭕", platforms: ["instagram", "facebook"] },
      { id: "thread", label: "Thread", icon: "🧵", platforms: ["twitter"] },
      { id: "carousel", label: "Carousel", icon: "🖼️", platforms: ["instagram", "threads", "linkedin"] },
      { id: "poll", label: "Poll", icon: "📊", platforms: ["twitter", "facebook", "linkedin"] },
      { id: "document", label: "Document", icon: "📄", platforms: ["linkedin"] },
    ];
    if (platforms.length === 0) return types;
    return types.filter(t => platforms.some(p => t.platforms.includes(p)));
  })();

  // Caption helpers
  const getCaption = (accountId: string) =>
    captions[accountId]?.customised ? captions[accountId].content : masterContent;

  const setCaption = (accountId: string, value: string) =>
    setCaptions(prev => ({ ...prev, [accountId]: { customised: true, content: value } }));

  const resetCaption = (accountId: string) =>
    setCaptions(prev => { const n = { ...prev }; delete n[accountId]; return n; });

  // Propagate master → all platform captions
  const applyMasterToAll = () => {
    const reset: Record<string, PlatformCaption> = {};
    selectedAccountIds.forEach(id => { reset[id] = { customised: false, content: masterContent }; });
    setCaptions(reset);
    toast({ title: "Master caption applied to all platforms" });
  };

  // Propagate one platform → all others
  const applyToAll = (sourceId: string) => {
    const source = getCaption(sourceId);
    setMasterContent(source);
    const reset: Record<string, PlatformCaption> = {};
    selectedAccountIds.forEach(id => { reset[id] = { customised: false, content: source }; });
    setCaptions(reset);
    toast({ title: "Caption applied to all platforms" });
  };

  const addHashtagToTab = (tag: string, accountId: string | "master") => {
    const cleaned = tag.replace(/^#/, "");
    if (accountId === "master") {
      setMasterContent(p => `${p} #${cleaned}`.trim());
    } else {
      setCaption(accountId, `${getCaption(accountId)} #${cleaned}`.trim());
    }
  };

  const { data: orgSettings } = useQuery({
    queryKey: ["org-settings-hashtags", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase.from("organizations" as any).select("settings").eq("id", orgId!).single() as any);
      return data?.settings as any;
    },
  });

  const coreHashtags: string[] = (orgSettings?.core_hashtags as string ?? "")
    .split(/\s+/).map((h: string) => h.trim()).filter((h: string) => h.startsWith("#"));

  const { data: orgMembers = [] } = useQuery({
    queryKey: ["assignable-members", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data: members } = await (supabase.from("organization_members" as any)
        .select("user_id, roles(name)").eq("org_id", orgId).eq("status", "active") as any);
      if (!members?.length) return [];
      const userIds = (members as any[]).map((m: any) => m.user_id);
      const { data: profiles } = await supabase.from("profiles").select("user_id, full_name").in("user_id", userIds);
      const map = Object.fromEntries((profiles || []).map((p: any) => [p.user_id, p.full_name]));
      return (members as any[]).map((m: any) => ({
        user_id: m.user_id, full_name: map[m.user_id] || "Team member", role: m.roles?.name,
      }));
    },
  });

  const fetchAccounts = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from("social_accounts").select("*").eq("user_id", user.id).eq("is_connected", true);
    setAccounts(data || []);
  };

  const fetchWorkflows = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from("approval_workflows" as any).select("id, name, is_active").eq("user_id", user.id).eq("is_active", true);
    setWorkflows((data as any) || []);
  };

  const toggleAccount = (id: string) =>
    setSelectedAccountIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Validation per platform
  const validationIssues = selectedAccounts.flatMap(acc => {
    const v = PlatformPublisher.validatePost(acc.platform as Platform, getCaption(acc.id), mediaUrls);
    return v.valid ? [] : [{ platform: acc.platform, label: PLATFORM_META[acc.platform]?.label ?? acc.platform, error: v.error ?? "Invalid" }];
  });

  const canPublish = selectedAccountIds.length > 0
    && selectedAccounts.some(a => getCaption(a.id).trim())
    && validationIssues.length === 0;

  const handlePredictBestTime = async () => {
    const acc = selectedAccounts[0];
    if (!acc) return;
    setLoadingBestTimes(true);
    try {
      const { data, error } = await supabase.functions.invoke("predict-best-time", { body: { platform: acc.platform.toLowerCase() } });
      if (error) throw error;
      setBestTimes(data?.bestTimes || []);
    } catch (e: any) {
      toast({ title: "Couldn't load best-time suggestions", description: e.message, variant: "destructive" });
    } finally { setLoadingBestTimes(false); }
  };

  const handleSave = async (postStatus: "draft" | "scheduled" | "published") => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast({ title: "Sign in required", variant: "destructive" }); return; }
    if (selectedAccountIds.length === 0) { toast({ title: "Select at least one platform", variant: "destructive" }); return; }
    if (!selectedAccounts.some(a => getCaption(a.id).trim())) { toast({ title: "Add caption content", variant: "destructive" }); return; }
    if (postStatus === "published" && validationIssues.length > 0) {
      toast({ title: "Fix errors before publishing", description: validationIssues.map(i => `${i.label}: ${i.error}`).join(" · "), variant: "destructive" });
      return;
    }

    setPublishing(true);
    const results: { platform: string; success: boolean; error?: string }[] = [];
    const warnings: string[] = [];

    try {
      for (const acc of selectedAccounts) {
        const caption = getCaption(acc.id);
        if (!caption.trim()) continue;

        const postData: any = {
          user_id: user.id,
          content: postType === "thread"
            ? threadTweets.filter(t => t.trim()).join("\n---\n")
            : caption.trim(),
          status: postStatus,
          social_account_id: acc.id,
          platform: acc.platform,
          platforms: [acc.platform],
          social_account_ids: [acc.id],
          media_urls: mediaUrls.length > 0 ? mediaUrls : null,
          scheduled_for: scheduledFor || null,
          is_ai_generated: aiAssisted,
          assigned_to: assignedTo || null,
          assigned_by: assignedTo ? user.id : null,
          assigned_at: assignedTo ? new Date().toISOString() : null,
          post_type: postType,
          ...(postType === "thread" ? { thread_tweets: threadTweets.filter(t => t.trim()) } : {}),
          ...(postType === "poll" ? { platform_options: { poll_options: pollOptions.filter(o => o.trim()), poll_duration_minutes: pollDuration } } : {}),
          ...(orgId ? { org_id: orgId } : {}),
        };

        const { data: post, error } = await supabase.from("posts").insert(postData).select().single() as any;
        if (error) { results.push({ platform: acc.platform, success: false, error: error.message }); continue; }

        if (mediaUrls.length > 0 && post) {
          const { error: mediaError } = await supabase.from("post_media").insert(
            mediaUrls.map((url, i) => ({ post_id: post.id, media_url: url, media_type: url.match(/\.(mp4|mov)$/i) ? "video" : "image", order_index: i }))
          );
          if (mediaError) warnings.push(`${acc.platform}: media attachment failed (${mediaError.message})`);
        }

        if (requiresApproval && selectedWorkflow && post) {
          const { data: wf } = await supabase.from("approval_workflows" as any).select("steps,chain_mode,org_id").eq("id", selectedWorkflow).single() as any;
          if (wf?.chain_mode) {
            const { data: approverId } = await (supabase as any).rpc("next_approver_in_chain", { p_org_id: wf.org_id ?? orgId, p_user_id: user.id });
            if (approverId) {
              const { error: apprError } = await supabase.from("post_approvals").insert({ post_id: post.id, workflow_id: selectedWorkflow, approver_id: approverId, status: "pending" } as any);
              if (apprError) {
                warnings.push(`${acc.platform}: approval request failed (${apprError.message})`);
              } else {
                const { error: notifyError } = await supabase.functions.invoke("notify-approver", { body: { post_id: post.id, workflow_id: selectedWorkflow, step_index: 0, approver_id: approverId } });
                if (notifyError) warnings.push(`${acc.platform}: approver wasn't notified (${notifyError.message})`);
              }
            }
          } else if (wf?.steps?.[0]) {
            const approverId = wf.steps[0].approver_id || user.id;
            const { error: apprError } = await supabase.from("post_approvals").insert({ post_id: post.id, workflow_id: selectedWorkflow, approver_id: approverId, status: "pending" } as any);
            if (apprError) {
              warnings.push(`${acc.platform}: approval request failed (${apprError.message})`);
            } else if (wf.steps[0].approver_id) {
              const { error: notifyError } = await supabase.functions.invoke("notify-approver", { body: { post_id: post.id, workflow_id: selectedWorkflow, step_index: 0, approver_id: wf.steps[0].approver_id } });
              if (notifyError) warnings.push(`${acc.platform}: approver wasn't notified (${notifyError.message})`);
            }
          }
        }

        if (assignedTo && post) {
          const { error: assignError } = await supabase.functions.invoke("assign-post", { body: { post_id: post.id, assignee_id: assignedTo, assigner_id: user.id, note: assignNote || undefined } });
          if (assignError) warnings.push(`${acc.platform}: assignment failed (${assignError.message})`);
        }

        if (postStatus === "published") {
          let result: { success: boolean; error?: string } = { success: false };

          if (postType === "reel" && acc.platform === "instagram") {
            const { error: fnErr } = await supabase.functions.invoke("publish-instagram-reel", { body: { postId: post.id, socialAccountId: acc.id } });
            result = fnErr ? { success: false, error: fnErr.message } : { success: true };
          } else if (postType === "story" && (acc.platform === "instagram" || acc.platform === "facebook")) {
            const { error: fnErr } = await supabase.functions.invoke("publish-instagram-story", { body: { postId: post.id, socialAccountId: acc.id } });
            result = fnErr ? { success: false, error: fnErr.message } : { success: true };
          } else if (postType === "thread" && acc.platform === "twitter") {
            const { error: fnErr } = await supabase.functions.invoke("publish-twitter-thread", { body: { postId: post.id, socialAccountId: acc.id } });
            result = fnErr ? { success: false, error: fnErr.message } : { success: true };
          } else if (postType === "document" && acc.platform === "linkedin") {
            const { error: fnErr } = await supabase.functions.invoke("publish-linkedin-document", { body: { postId: post.id, socialAccountId: acc.id } });
            result = fnErr ? { success: false, error: fnErr.message } : { success: true };
          } else {
            result = await PlatformPublisher.publish(post.id, caption.trim(), mediaUrls, acc.id);
          }

          results.push({ platform: acc.platform, success: result.success, error: result.error });
        } else {
          results.push({ platform: acc.platform, success: true });
        }
      }

      const succeeded = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);

      if (postStatus === "published") {
        toast({
          title: succeeded > 0 ? `Published to ${succeeded} platform${succeeded > 1 ? "s" : ""}` : "Publishing failed",
          description: failed.length > 0 ? `Failed: ${failed.map(f => `${f.platform}: ${f.error}`).join(" · ")}` : undefined,
          variant: failed.length > 0 && succeeded === 0 ? "destructive" : "default",
        });
      } else {
        toast({ title: postStatus === "scheduled" ? `Scheduled on ${results.length} platform${results.length > 1 ? "s" : ""}` : "Draft saved" });
      }

      if (warnings.length > 0) {
        toast({ title: "Post saved with issues", description: warnings.join(" · "), variant: "destructive" });
      }

      setMasterContent(""); setCaptions({}); setSelectedAccountIds([]);
      setScheduledFor(""); setMediaUrls([]); setAiAssisted(false);
      setAssignedTo(null); setAssignNote(""); setBestTimes([]);
      setPostType("standard"); setThreadTweets(["", ""]); setPollOptions(["", ""]);
    } finally {
      setPublishing(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const multiPlatform = selectedAccountIds.length > 1;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold mb-1">Create Post</h2>
        <p className="text-sm text-muted-foreground">Compose for one or multiple platforms — each can have its own caption, language, and AI generation</p>
      </div>

      <Card className="p-5 space-y-5">

        {/* ── 1. Platform picker ──────────────────────────────────────── */}
        <div className="space-y-2">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Select Platforms
          </Label>
          {accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-1">No connected accounts — go to Connected Accounts to link your profiles.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {accounts.map(acc => {
                const meta = PLATFORM_META[acc.platform] ?? { icon: AtSign, color: "text-muted-foreground", bg: "bg-muted/30", ring: "", limit: 0, warning: 0, label: acc.platform, hint: "" };
                const Icon = meta.icon;
                const selected = selectedAccountIds.includes(acc.id);
                return (
                  <button
                    key={acc.id}
                    onClick={() => toggleAccount(acc.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all text-sm font-medium ${
                      selected
                        ? `${meta.bg} border-current ${meta.color} shadow-sm ring-1 ${meta.ring}`
                        : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${selected ? meta.color : ""}`} />
                    <span>{meta.label}</span>
                    <span className="text-[10px] opacity-60">@{acc.account_handle}</span>
                    {selected && <CheckCircle2 className="w-3.5 h-3.5 ml-0.5" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── 2. Post Type selector ───────────────────────────────────── */}
        {selectedAccountIds.length > 0 && availablePostTypes.length > 1 && (
          <div className="space-y-2">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Post Type</Label>
            <div className="flex flex-wrap gap-2">
              {availablePostTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setPostType(type.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all ${
                    postType === type.id
                      ? "border-primary bg-primary/10 text-primary font-medium shadow-sm"
                      : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  <span>{type.icon}</span>
                  <span>{type.label}</span>
                </button>
              ))}
            </div>

            {/* Thread composer */}
            {postType === "thread" && (
              <div className="space-y-2 mt-2 p-3 bg-muted/30 rounded-lg border">
                <p className="text-xs text-muted-foreground font-medium">Thread Tweets — each tweet max 280 chars</p>
                {threadTweets.map((tweet, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <span className="text-xs text-muted-foreground w-5 pt-2 shrink-0">{i + 1}.</span>
                    <div className="flex-1">
                      <Textarea
                        value={tweet}
                        onChange={e => setThreadTweets(prev => prev.map((t, j) => j === i ? e.target.value : t))}
                        placeholder={i === 0 ? "Start your thread here…" : `Tweet ${i + 1}…`}
                        rows={2}
                        maxLength={280}
                        className="text-sm resize-none"
                      />
                      <div className="flex justify-between mt-0.5">
                        <span className={`text-[10px] ${tweet.length > 260 ? "text-amber-500" : "text-muted-foreground/50"}`}>{tweet.length}/280</span>
                        {threadTweets.length > 2 && (
                          <button onClick={() => setThreadTweets(prev => prev.filter((_, j) => j !== i))} className="text-[10px] text-destructive">remove</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setThreadTweets(prev => [...prev, ""])}>
                  <Plus className="w-3 h-3 mr-1" /> Add tweet
                </Button>
              </div>
            )}

            {/* Poll composer */}
            {postType === "poll" && (
              <div className="space-y-2 mt-2 p-3 bg-muted/30 rounded-lg border">
                <p className="text-xs text-muted-foreground font-medium">Poll Options (2–4)</p>
                {pollOptions.map((opt, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      value={opt}
                      onChange={e => setPollOptions(prev => prev.map((o, j) => j === i ? e.target.value : o))}
                      placeholder={`Option ${i + 1}`}
                      maxLength={25}
                      className="h-8 text-sm"
                    />
                    {pollOptions.length > 2 && (
                      <button onClick={() => setPollOptions(prev => prev.filter((_, j) => j !== i))}>
                        <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                      </button>
                    )}
                  </div>
                ))}
                {pollOptions.length < 4 && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPollOptions(prev => [...prev, ""])}>
                    <Plus className="w-3 h-3 mr-1" /> Add option
                  </Button>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <Label className="text-xs text-muted-foreground">Duration:</Label>
                  <Select value={String(pollDuration)} onValueChange={v => setPollDuration(Number(v))}>
                    <SelectTrigger className="h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="360">6 hours</SelectItem>
                      <SelectItem value="720">12 hours</SelectItem>
                      <SelectItem value="1440">1 day</SelectItem>
                      <SelectItem value="4320">3 days</SelectItem>
                      <SelectItem value="10080">7 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── 3. Validation banner ────────────────────────────────────── */}
        {validationIssues.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="space-y-0.5">
              {validationIssues.map(i => (
                <div key={i.platform}><strong>{i.label}:</strong> {i.error}</div>
              ))}
            </AlertDescription>
          </Alert>
        )}

        {/* ── 3. Caption editor ───────────────────────────────────────── */}
        {selectedAccountIds.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Caption</Label>
              {multiPlatform && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground h-7 px-2"
                  onClick={applyMasterToAll}
                >
                  <Share2 className="w-3 h-3 mr-1" /> Sync master → all
                </Button>
              )}
            </div>

            {multiPlatform ? (
              /* ── Multi-platform: tabbed per-platform editor ─────────── */
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="flex-wrap h-auto bg-muted/40 gap-0.5 p-1">
                  {/* Master tab */}
                  <TabsTrigger value="master" className="text-xs px-3 py-1.5 rounded-md">
                    <Globe className="w-3 h-3 mr-1" /> Master
                  </TabsTrigger>
                  {/* Per-platform tabs */}
                  {selectedAccounts.map(acc => {
                    const meta = PLATFORM_META[acc.platform];
                    const Icon = meta?.icon ?? AtSign;
                    const customised = captions[acc.id]?.customised ?? false;
                    const ci = charInfo(getCaption(acc.id), acc.platform);
                    return (
                      <TabsTrigger key={acc.id} value={acc.id} className="text-xs px-3 py-1.5 rounded-md gap-1">
                        <Icon className={`w-3 h-3 ${meta?.color ?? ""}`} />
                        <span>{meta?.label ?? acc.platform}</span>
                        {customised && <span className="w-1.5 h-1.5 rounded-full bg-primary ml-0.5" title="Custom caption" />}
                        {ci.over && <span className="w-1.5 h-1.5 rounded-full bg-destructive ml-0.5" />}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {/* Master tab content */}
                <TabsContent value="master" className="mt-3 space-y-3">
                  <div className="text-[11px] text-muted-foreground">
                    Default caption shared across all selected platforms. Switch to a platform tab to write a custom version for that platform only.
                  </div>

                  <CaptionToolbar
                    accountId="master"
                    platform={selectedAccounts[0]?.platform ?? "instagram"}
                    content={masterContent}
                    coreHashtags={coreHashtags}
                    onApply={text => { setMasterContent(text); setAiAssisted(true); }}
                    onAddHashtag={tag => addHashtagToTab(tag, "master")}
                  />

                  <Textarea
                    placeholder="Write your master caption here… it will be used for all platforms unless you customise individually."
                    value={masterContent}
                    onChange={e => setMasterContent(e.target.value)}
                    rows={6}
                    className="resize-none"
                  />

                  {/* Per-platform limit pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {selectedAccounts.map(acc => {
                      const ci = charInfo(masterContent, acc.platform);
                      if (!ci.limit) return null;
                      return (
                        <span
                          key={acc.id}
                          className={`text-[10px] px-2 py-0.5 rounded-full border font-mono ${
                            ci.over ? "border-destructive text-destructive" :
                            ci.near ? "border-amber-400 text-amber-600" :
                            "border-border text-muted-foreground"
                          }`}
                        >
                          {PLATFORM_META[acc.platform]?.label}: {ci.count.toLocaleString()} / {ci.limit.toLocaleString()}
                          {ci.over ? " ✕" : ""}
                        </span>
                      );
                    })}
                  </div>
                </TabsContent>

                {/* Per-platform tab content */}
                {selectedAccounts.map(acc => {
                  const meta = PLATFORM_META[acc.platform];
                  const cap = getCaption(acc.id);
                  const customised = captions[acc.id]?.customised ?? false;
                  const ci = charInfo(cap, acc.platform);

                  return (
                    <TabsContent key={acc.id} value={acc.id} className="mt-3 space-y-3">
                      {/* Status + actions row */}
                      <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs ${meta?.bg ?? "bg-muted/40"}`}>
                        <span className={`font-medium ${meta?.color}`}>
                          {customised ? "✏️ Custom caption" : "🔗 Using master caption"}
                        </span>
                        <div className="flex gap-1.5">
                          {customised && (
                            <>
                              <button
                                className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                                onClick={() => resetCaption(acc.id)}
                              >
                                Reset to master
                              </button>
                              <span className="text-muted-foreground">·</span>
                              <button
                                className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                                onClick={() => applyToAll(acc.id)}
                              >
                                Use for all
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Platform hint */}
                      {meta?.hint && (
                        <p className="text-[11px] text-muted-foreground italic">{meta.hint}</p>
                      )}

                      {/* Toolbar — platform-specific */}
                      <CaptionToolbar
                        accountId={acc.id}
                        platform={acc.platform}
                        content={cap}
                        coreHashtags={coreHashtags}
                        onApply={text => { setCaption(acc.id, text); setAiAssisted(true); }}
                        onAddHashtag={tag => addHashtagToTab(tag, acc.id)}
                        compact
                      />

                      {/* Textarea */}
                      <Textarea
                        placeholder={customised
                          ? `Custom caption for ${meta?.label ?? acc.platform}…`
                          : `Type here to create a custom caption for ${meta?.label ?? acc.platform}, or use the buttons above to generate/translate…`
                        }
                        value={cap}
                        onChange={e => setCaption(acc.id, e.target.value)}
                        rows={6}
                        className="resize-none"
                      />

                      {/* Char counter + progress */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className={ci.over ? "text-destructive font-medium" : ci.near ? "text-amber-500" : "text-muted-foreground"}>
                            {ci.count.toLocaleString()} / {ci.limit.toLocaleString()} characters
                            {ci.over && " — exceeds limit!"}
                            {ci.near && !ci.over && " — approaching limit"}
                          </span>
                          <span className="text-muted-foreground">{PlatformPublisher.countHashtags(cap)} hashtags</span>
                        </div>
                        {ci.limit > 0 && (
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${ci.over ? "bg-destructive" : ci.near ? "bg-amber-400" : "bg-primary"}`}
                              style={{ width: `${ci.pct}%` }}
                            />
                          </div>
                        )}
                      </div>

                      {ci.over && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            {meta?.label} has a {meta?.limit?.toLocaleString()} character limit. Shorten this caption or it will be blocked on publish.
                          </AlertDescription>
                        </Alert>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            ) : (
              /* ── Single platform: simple editor ─────────────────────── */
              <div className="space-y-3">
                {selectedAccounts[0] && PLATFORM_META[selectedAccounts[0].platform]?.hint && (
                  <p className="text-[11px] text-muted-foreground italic">{PLATFORM_META[selectedAccounts[0].platform].hint}</p>
                )}

                <CaptionToolbar
                  accountId="master"
                  platform={selectedAccounts[0]?.platform ?? "instagram"}
                  content={masterContent}
                  coreHashtags={coreHashtags}
                  onApply={text => { setMasterContent(text); setAiAssisted(true); }}
                  onAddHashtag={tag => addHashtagToTab(tag, "master")}
                />

                <Textarea
                  placeholder={
                    selectedAccounts[0]
                      ? `Write your ${PLATFORM_META[selectedAccounts[0].platform]?.label ?? selectedAccounts[0].platform} caption here…`
                      : "Select a platform first, then write your caption…"
                  }
                  value={masterContent}
                  onChange={e => setMasterContent(e.target.value)}
                  rows={7}
                  className="resize-none"
                />

                {selectedAccounts[0] && (() => {
                  const ci = charInfo(masterContent, selectedAccounts[0].platform);
                  return (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className={ci.over ? "text-destructive font-medium" : ci.near ? "text-amber-500" : "text-muted-foreground"}>
                          {ci.count.toLocaleString()} / {ci.limit.toLocaleString()} characters
                          {ci.over && " — exceeds limit!"}
                          {ci.near && !ci.over && " — approaching limit"}
                        </span>
                        <span className="text-muted-foreground">{PlatformPublisher.countHashtags(masterContent)} hashtags</span>
                      </div>
                      {ci.limit > 0 && (
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${ci.over ? "bg-destructive" : ci.near ? "bg-amber-400" : "bg-primary"}`}
                            style={{ width: `${ci.pct}%` }}
                          />
                        </div>
                      )}
                      {ci.over && (
                        <Alert variant="destructive" className="mt-1">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            {PLATFORM_META[selectedAccounts[0].platform]?.label} has a {ci.limit.toLocaleString()} character limit.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ── 4. Media ────────────────────────────────────────────────── */}
        <div className="space-y-2">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Media (Optional)</Label>
          <MediaUpload onMediaUploaded={setMediaUrls} />
        </div>

        {/* ── 5. Schedule ─────────────────────────────────────────────── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Schedule (Optional)</Label>
            {selectedAccountIds.length > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={handlePredictBestTime} disabled={loadingBestTimes}>
                {loadingBestTimes ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Best times
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Input type="datetime-local" value={scheduledFor} onChange={e => setScheduledFor(e.target.value)} className="flex-1 text-sm" />
            {scheduledFor && <Button variant="ghost" size="sm" className="text-xs" onClick={() => setScheduledFor("")}>Clear</Button>}
          </div>
          {bestTimes.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <span className="text-[10px] text-muted-foreground w-full">Click to auto-fill:</span>
              {bestTimes.map((t, i) => (
                <Badge key={i} variant="outline" className="text-xs cursor-pointer hover:bg-primary/10" onClick={() => setScheduledFor(t)}>
                  {t}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* ── 6. Advanced ─────────────────────────────────────────────── */}
        <div>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground px-0 h-7" onClick={() => setShowAdvanced(p => !p)}>
            {showAdvanced ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            {showAdvanced ? "Hide" : "Show"} advanced options
          </Button>

          {showAdvanced && (
            <div className="mt-3 space-y-4 border-t border-border pt-4">
              <div className="flex justify-between items-center">
                <Label className="text-xs text-muted-foreground">Design a graphic</Label>
                <CanvaDesignButton
                  defaultType={selectedAccounts[0]?.platform ? `${selectedAccounts[0].platform}_post` : "instagram_post"}
                  label="Open in Canva"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={aiAssisted} onCheckedChange={setAiAssisted} id="ai-flag" />
                <Label htmlFor="ai-flag" className="text-xs cursor-pointer flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Mark as AI-assisted
                </Label>
              </div>

              {(orgMembers as any[]).length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Assign to team member</Label>
                  <select value={assignedTo || ""} onChange={e => setAssignedTo(e.target.value || null)}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background">
                    <option value="">Unassigned</option>
                    {(orgMembers as any[]).map((m: any) => (
                      <option key={m.user_id} value={m.user_id}>{m.full_name}{m.role ? `, ${m.role}` : ""}</option>
                    ))}
                  </select>
                  {assignedTo && (
                    <Input value={assignNote} onChange={e => setAssignNote(e.target.value)}
                      placeholder="Note for assignee (optional)" className="text-sm mt-1" />
                  )}
                </div>
              )}

              {workflows.length > 0 && (
                <div className="space-y-2 p-3 rounded-lg border border-border bg-muted/20">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Require approval before publishing</Label>
                    <Switch checked={requiresApproval} onCheckedChange={setRequiresApproval} />
                  </div>
                  {requiresApproval && (
                    <Select value={selectedWorkflow} onValueChange={setSelectedWorkflow}>
                      <SelectTrigger className="text-sm"><SelectValue placeholder="Select workflow…" /></SelectTrigger>
                      <SelectContent>
                        {workflows.map((wf: any) => <SelectItem key={wf.id} value={wf.id}>{wf.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── 7. Action buttons ───────────────────────────────────────── */}
        <div className="flex gap-2 pt-2 border-t border-border">
          <Button variant="outline" className="flex-1" onClick={() => handleSave("draft")} disabled={publishing || selectedAccountIds.length === 0}>
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => handleSave("scheduled")} disabled={!scheduledFor || publishing || selectedAccountIds.length === 0}>
            <Clock className="w-4 h-4 mr-2" />
            {scheduledFor ? "Schedule" : "Set time first"}
          </Button>
          <Button
            className="gradient-primary flex-1"
            onClick={() => handleSave("published")}
            disabled={publishing || !canPublish}
          >
            {publishing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publishing…</>
              : <><Send className="w-4 h-4 mr-2" />Publish{selectedAccountIds.length > 1 ? ` (${selectedAccountIds.length})` : ""}</>
            }
          </Button>
        </div>

        {selectedAccountIds.length > 0 && (
          <p className="text-[11px] text-center text-muted-foreground">
            {selectedAccountIds.length === 1
              ? `Publishing to ${PLATFORM_META[selectedAccounts[0]?.platform]?.label ?? selectedAccounts[0]?.platform} · @${selectedAccounts[0]?.account_handle}`
              : `Publishing to ${selectedAccountIds.length} platforms: ${selectedAccounts.map(a => PLATFORM_META[a.platform]?.label ?? a.platform).join(", ")}`
            }
          </p>
        )}
      </Card>
    </div>
  );
};
